import { ToolResultCard } from '@/components/ai-agent/ToolResultCard';
import { AIFollowUpChip } from './AIFollowUpChip';

type SuggestedAction = string | { label?: string; path?: string; prompt?: string };

function extractSuggestedActions(data: unknown): SuggestedAction[] {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return [];
  const actions = (data as Record<string, unknown>).suggestedActions;
  return Array.isArray(actions) ? (actions as SuggestedAction[]) : [];
}

function hasRichVisual(data: unknown): boolean {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return false;
  const d = data as Record<string, unknown>;
  return Boolean(d.dataTable || d.chartSpec || d.kpiCards);
}

export interface AIResponseBlockProps {
  toolName?: string;
  data: unknown;
  onActionPrompt?: (prompt: string) => void;
  onPin?: (input: {
    sourceType: 'kpi' | 'chart' | 'table' | 'report' | 'insight';
    title: string;
    payload: Record<string, unknown>;
  }) => void;
  onSaveUdq?: (input: {
    intent: string;
    filters?: Record<string, unknown>;
    query?: string;
    title?: string;
    displayColumns?: string[];
  }) => void;
}

export function AIResponseBlock({ toolName, data, onActionPrompt, onPin, onSaveUdq }: AIResponseBlockProps) {
  const suggested = extractSuggestedActions(data);
  const rich = hasRichVisual(data);

  const pinPayload = () => {
    if (!rich || !data || typeof data !== 'object') return;
    const d = data as Record<string, unknown>;
    let sourceType: 'kpi' | 'chart' | 'table' | 'report' | 'insight' = 'insight';
    if (d.chartSpec) sourceType = 'chart';
    else if (d.dataTable) sourceType = 'table';
    else if (d.kpiCards) sourceType = 'kpi';
    const title =
      (typeof d.intent === 'string' ? d.intent.replace(/_/g, ' ') : undefined) ||
      (d.dataTable as { title?: string } | undefined)?.title ||
      'Pinned insight';
    onPin?.({ sourceType, title, payload: d as Record<string, unknown> });
  };

  const saveUdq = () => {
    if (!data || typeof data !== 'object') return;
    const d = data as Record<string, unknown>;
    if (typeof d.intent !== 'string') return;
    const table = d.dataTable as { title?: string; columns?: Array<{ key: string }> } | undefined;
    onSaveUdq?.({
      intent: d.intent,
      filters:
        d.sourceContext && typeof d.sourceContext === 'object'
          ? ((d.sourceContext as { filters?: Record<string, unknown> }).filters as Record<string, unknown> | undefined)
          : undefined,
      title: table?.title || d.intent,
      displayColumns: table?.columns?.map((c) => c.key),
    });
  };

  return (
    <div>
      <ToolResultCard
        toolName={toolName ?? 'tool'}
        data={data}
        compact={false}
        onActionPrompt={onActionPrompt}
        onPin={onPin}
        onSaveUdq={onSaveUdq}
      />
      {rich ? (
        <div className="ai-follow-up-row" role="group" aria-label="Follow-up actions">
          {typeof (data as { intent?: string }).intent === 'string' && onSaveUdq ? (
            <AIFollowUpChip label="Save as UDQ" onClick={saveUdq} />
          ) : null}
          <AIFollowUpChip label="Pin to dashboard" onClick={pinPayload} />
          {suggested.map((action, idx) => {
            const isText = typeof action === 'string';
            const label = isText ? action : String(action?.label || '').trim();
            const prompt = isText ? action : String(action?.prompt || '').trim();
            if (!label || !prompt || !onActionPrompt) return null;
            return <AIFollowUpChip key={`${label}-${idx}`} label={label} onClick={() => onActionPrompt(prompt)} />;
          })}
        </div>
      ) : null}
    </div>
  );
}
